// import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import { Backdrop, CircularProgress } from '@material-ui/core';
import {
  AssignedProductLite,
  isAsgnProduct,
  productImg,
  ProductLite,
  AssignProductReqBlank,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import { ProductFromSearchV1 } from 'app/core/api/product/api-product.types';
import { MissingItemError } from 'app/core/errors/error-variants';
import ProductPictureOrDefault from 'app/shared/components/app-image/ProductPictureOrDefault';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import TileCardButton from 'app/shared/frames/TileCardButton';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { isEnumMember, PropsOf } from 'app/tools/general.types';
import { Markup } from 'interweave';
import React, { ReactNode, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill';
import styled, { css } from 'styled-components/macro';
import { Except, Merge } from 'type-fest';
import { useHistory } from 'react-router-dom';
import { useAssignProductBlank } from 'app/core/api/product-assignment/api-product-assignment';
import { makeStyles } from '@material-ui/core/styles';
import ConsumerRoutePath from '../../../ConsumerRoutePath';
import IconBox from './IconBox';

const NameBox = styled.div`
  position: relative;
  padding: ${styledFuncs.pad(0.5)};
  overflow: hidden;
  display: flex;
`;

const NameFadeOverlay = styled.div`
  position: absolute;
  bottom: -1px;
  width: 100%;
  height: 1.2em;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 1) 90%
  );
`;

const Name = styled.div`
  hyphens: auto;
  word-break: break-word;
  align-self: center;
  max-height: 100%;
`;

const PicOrDefault = styled(ProductPictureOrDefault)`
  padding: ${styledFuncs.pad(0.5)};
`;

const StyledIconBox = styled(IconBox)``;

const CardText = styled.div`
  font-weight: 600;
`;

const IconButtonText = styled(CardText)`
  margin-top: ${styledFuncs.pad()};
`;

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

// (don't ask)
export const getCardTextFontPx = ({
  $width,
}: CardButtonOpts): string => `${$width ** 0.3 * 3}px`;
export const getWideCardTextFontPx = ({
  $width,
}: CardButtonOpts): string =>
  getCardTextFontPx({ $width: $width * 0.7 });

const getAddBtnTextFontMax = ({ $width }: CardButtonOpts): number =>
  parseFloat(getWideCardTextFontPx({ $width: $width * 2 + 40 }));
const getAddBtnTextFontPx = ({ $width }: CardButtonOpts): string =>
  `${Math.min($width * 0.12, getAddBtnTextFontMax({ $width }))}px`;

type CardButtonOpts = {
  $variant?: 'wide' | 'wide-no-icons';
  $width: number;
};
const ProductListCardButton = styled(TileCardButton)<CardButtonOpts>`
  .MuiButton-label > div {
    flex-flow: row-reverse wrap;

    ${NameBox} {
      height: 60%;
      width: 55%;
    }
    ${PicOrDefault} {
      height: 60%;
      width: 45%;
    }
    ${StyledIconBox} {
      height: 40%;
      width: 100%;
    }
    ${CardText} {
      font-size: ${getCardTextFontPx};
      line-height: 1.4;
    }
    ${IconButtonText} {
      font-size: ${getAddBtnTextFontPx};
    }

    ${p =>
      p.wide &&
      css`
        flex-direction: column;

        ${NameBox} {
          height: 45%;
          width: 60%;
        }
        ${PicOrDefault} {
          height: 100%;
          width: 40%;
        }
        ${StyledIconBox} {
          margin: ${styledFuncs.pad(0.4)} 0;
          height: calc(55% - ${styledFuncs.pad(0.8)});
          width: 60%;
        }

        ${CardText} {
          font-size: ${getWideCardTextFontPx(p)};
          line-height: 1.4;
        }
      `}

    ${p =>
      p.wide &&
      p.$variant === 'wide-no-icons' &&
      css`
        ${NameBox} {
          height: 100%;
          width: 60%;
          padding-left: ${styledFuncs.pad()};
          padding-right: ${styledFuncs.pad()};
          ${styledMixins.flexCenter}
          align-items: flex-start;
          > div {
            max-height: 100%;
          }
        }
        ${PicOrDefault} {
          height: 100%;
          width: 40%;
          padding: ${styledFuncs.pad()};
        }
      `}
  }
`;

const IconTileButton = styled(ProductListCardButton)`
  .MuiButton-label > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-weight: 600;
    > * {
      align-self: center;
    }
  }
`;

type Props = Merge<
  Except<PropsOf<typeof Button>, 'ref'>,
  {
    variant?: 'wide' | 'wide-no-icons';
    item?: AssignedProductLite | ProductLite | ProductFromSearchV1;
    iconButtonIcon?: IconLib | ReactNode;
    iconButtonText?: string;
    iconButtonColor?: string;
  }
>;

export default function ProductCard({
  item,
  variant,
  iconButtonIcon,
  iconButtonText,
  iconButtonColor,
  ...buttonProps
}: Props): JSX.Element {
  const classes = useStyles();
  const history = useHistory();
  const [open, setOpen] = useState<boolean>(false);
  const { request$: assignProductBlank$ } = useAssignProductBlank();
  const { width = 300, ref } = useResizeDetector();

  const wide = variant === 'wide' || variant === 'wide-no-icons';

  if (item && !iconButtonIcon) {
    const product = isAsgnProduct(item) ? item.productDetails : item;

    // const upcIdQuery = isSearchResult(item)
    //   ? { [UrlQueryKey.IS_NEW_DATABASE_PRODUCT]: 'true' }
    //   : ({} as Obj<never>);
    // const clickDestination = isAsgnProduct(item)
    //   ? `${ConsumerRoutePath.VIEW_ASSIGNED_PRODUCT}/${item.id}`
    //   : {
    //       pathname: `${ConsumerRoutePath.ADD_ASSIGNED_PRODUCT_DETAILS}`,
    //       query: {
    //         [UrlQueryKey.PRODUCT_VARIANT_ID]: item.id,
    //         [UrlQueryKey.PRODUCT_VARIANT_NAME]: item.name,
    //         ...upcIdQuery,
    //       },
    //     };

    const clickDestinationNew = () => {
      if (isAsgnProduct(item)) {
        history.push(
          `${ConsumerRoutePath.VIEW_ASSIGNED_PRODUCT}/${item.id}`
        );
      } else {
        setOpen(true);
        // const productIdentifier: Except<
        //   AssignProductReq,
        //   'productVariant'
        // > = isSearchResult(item)
        //   ? { upcProductId: item.id.toString() }
        //   : { productVariant: item.id.toString() };
        const productIdentifier: AssignProductReqBlank = {
          upcProductId: item.id.toString(),
        };

        // const partialData: Except<
        //   AssignProductReq,
        //   'productVariant'
        // > = {
        // };

        assignProductBlank$(productIdentifier).subscribe(res => {
          setOpen(false);
          history.push(
            `${ConsumerRoutePath.VIEW_ASSIGNED_PRODUCT}/${res.id}`
          );
        });
      }
    };

    const picWidth = wide ? 0.4 * width : 0.45 * width;

    return (
      <ProductListCardButton
        $variant={variant}
        $width={width}
        // to={clickDestination}
        onClick={clickDestinationNew}
        {...{ ref, wide }}
        {...buttonProps}
      >
        <PicOrDefault
          productId={product.id}
          src={productImg(item)}
          alt={`${product.name ?? ''}`}
          sideSizeForCornerRadius={`${picWidth}px`}
        />
        <NameBox>
          <Name>
            <CardText>
              <Markup content={product.name} />
            </CardText>
          </Name>
          <NameFadeOverlay />
        </NameBox>
        {isAsgnProduct(item) && (
          <StyledIconBox
            assignedProduct={item}
            wide={variant === 'wide'}
          />
        )}
        <Backdrop className={classes.backdrop} open={open}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </ProductListCardButton>
    );
  } else if (iconButtonIcon) {
    return (
      <IconTileButton
        color="primary"
        variant="contained"
        $width={width}
        css={css`
          ${iconButtonColor &&
          styledMixins.buttonColor(iconButtonColor)}
        `}
        aria-label={iconButtonText?.toLowerCase()}
        {...buttonProps}
        {...{ ref, wide }}
      >
        {isEnumMember(iconButtonIcon, IconLib) ? (
          <Icon
            icon={iconButtonIcon}
            size={Math.min(width * 0.5, 100)}
          />
        ) : (
          <Icon
            imgNode={iconButtonIcon}
            size={Math.min(width * 0.5, 100)}
          />
        )}

        <IconButtonText>{iconButtonText}</IconButtonText>
      </IconTileButton>
    );
  } else {
    throw new MissingItemError('product item');
  }
}
